import React, {
  ComponentType,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import Cookies from 'js-cookie';

import { setError as setErrorAction } from '@/store/modules/error/actions';
import {
  setUser as setUserAction,
  setUserNewRegistration as setUserNewRegistrationAction,
} from '@/store/modules/user/actions';

import { ampli, RegistrationReferralCodeEnteredSuccessfulProperties } from '@/ampli';
import { removeReferral } from '@/api/request';
import * as api from '@/api/users';
import Loader from '@/components/atoms/loader';
import { unsubscribeToPrivateChannels } from '@/components/contexts/pusher-events';
import { UserLoginRequest, UserRegistrationRequest } from '@/interfaces/user';
import type { AmpliService } from '@/services/ampli/service';
import { auth0Service } from '@/services/auth/auth0-service';
import type { AuthService } from '@/services/auth/service';
import type { DeviceService } from '@/services/device/service';
import geoComplyManager from '@/services/location/geocomply/manager';
import type { LoggingService } from '@/services/logging/service';
import eventLogger from '@/utilities/analytics';
import { AUTH0_SESSION_REFRESH, UD_THEME } from '@/utilities/constants';
import { AppError } from '@/utilities/errors';
import {
  AUTH0_ERRORS,
  ERROR_CODES,
  ERROR_MESSAGES,
  ERROR_NAMES,
} from '@/utilities/errors/constants';
import errorLogger from '@/utilities/errors/logger';
import useQuery from '@/utilities/hooks/use-query';

import styles from './styles.scss';

export interface AuthContextValue {
  confirmMfaCode: ({
    bindingCode,
    mfaToken,
    oobCode,
  }: {
    bindingCode: string;
    mfaToken: string;
    oobCode: string;
  }) => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
  loginUser: (args: UserLoginRequest) => Promise<void>;
  logoutUser: () => Promise<void>;
  registerUser: ({
    ampliPromotionType,
    promoCode,
    userRegistrationObject,
  }: {
    ampliPromotionType: RegistrationReferralCodeEnteredSuccessfulProperties['promotion_type'];
    promoCode: string;
    userRegistrationObject: UserRegistrationRequest;
  }) => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const context = useContext<AuthContextValue | undefined>(AuthContext);

  if (!context) {
    throw new Error(`AuthProvider context is undefined,
    please verify you are calling useAuth()
    as child of a <AuthProvider> component.`);
  }

  return context;
};

export function withAuth<P>(
  Component: React.ComponentType<P>
): ComponentType<Omit<P, keyof AuthContextValue>> {
  return (props) => {
    const auth = useAuth();

    if (auth.isLoading) {
      return <Loader className={styles.loader} />;
    }

    return <Component {...(props as P)} {...auth} />;
  };
}

const AuthContextProvider = ({
  ampliService,
  authService,
  children,
  deviceService,
  loggingService,
  setError,
  setUser,
  setUserNewRegistration,
}: PropsWithChildren<{
  ampliService: AmpliService | undefined;
  authService: AuthService;
  deviceService: DeviceService;
  loggingService: LoggingService | undefined;
  setError: typeof setErrorAction;
  setUser: typeof setUserAction;
  setUserNewRegistration: typeof setUserNewRegistrationAction;
}>) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isMobilePage = /^\/m\//.test(location.pathname);
  const queryParams = useQuery();
  // API errors may include a "cta url" that can be used to direct users to a specific URL
  // in the event of an error. One such URL is: `/login?force=true`
  // When the `force` query param is present and `true`, we need to logout the user
  // and redirect them to the login page.
  const forceLogout = queryParams.get('force') === 'true';

  if (forceLogout) {
    // When force logout is true, we logout the user without updating local state.
    // Then invoke a full page navigation to `/login` instead of just using `navigate()`,
    // which only updates the browser history. This helps ensure that we reset app state
    // after logging out, and avoid any potential issues with infinite redirects.
    // Also be mindful that we render a loader while `forceLogout` is true, so the app
    // doesn't try to render in an inaccurate state.
    // Note: in general, redirecting from within the render path is not a recommended pattern.
    // Ideally, we'd handle this force logout scenario _before_ the app is rendered, but
    // that requires extracting most of the code in `AuthProviderEntity` into a separate
    // service/module, which has many downstream implications in the app.
    // For now, this is the simplest solution.
    logoutUserInternal({ ampliService, authService, loggingService }).then(() => {
      window.location.href = '/login';
    });
  }

  const logoutUser = useCallback(async (): Promise<void> => {
    await logoutUserInternal({ ampliService, authService, loggingService });
    setIsAuthenticated(false);
    // review: clearing the user state value here seems a bit out of place.
    // We would potentially want to have something observing authentication state
    // and clearing the user state in response to authentication state change.
    // We don't really have the mechanisms in place for that right now though,
    // so this is good enough for now.
    // Also note: `setUser(undefined)` doesn't actually set the user state to `undefined`,
    // instead it resets the user state to an initial "empty" object. i.e. `state.user`
    // is defined, but all/most of its properties are null or empty.
    setUser(undefined);
    setIsLoading(false);
  }, [setUser, ampliService, authService, loggingService]);

  const confirmMfaCode = useCallback(
    async ({
      bindingCode,
      mfaToken,
      oobCode,
    }: {
      bindingCode: string;
      mfaToken: string;
      oobCode: string;
    }): Promise<void> => {
      try {
        await api.confirmMfaCode({ bindingCode, mfaToken, oobCode });
        setIsAuthenticated(true);

        ampli.loginSuccessful({
          'login-type': 'Manual',
          product_experience: 'fantasy',
        });
      } catch (e) {
        if (e?.status === 403 && e.title === AUTH0_ERRORS.INVALID_GRANT) {
          setError({ ...e, title: AUTH0_ERRORS.INVALID_MFA_CODE, type: 'toast' });
        } else {
          setError({ ...e, type: 'toast' });
        }
        ampli.loginUnsuccessful({ error_text: e.message || e.title });
      }
    },
    [setError]
  );

  const loginUser = useCallback(
    async (userLoginObject: UserLoginRequest): Promise<void> => {
      try {
        await api.loginUserAuth0({ userLoginObject, deviceId: deviceService.getDeviceId() });
        setIsAuthenticated(true);

        ampli.loginSuccessful({
          'login-type': 'Manual',
          product_experience: 'fantasy',
        });
      } catch (e) {
        if (e?.status === 403 && e.title === 'mfa_required') {
          setError({ ...e, type: 'mfaRequired' });
          // this is a special case for MFA
          // The login is not unsuccessful yet, so we don't want to log it
        } else {
          if (e?.status === 422) {
            setError({ ...e, type: 'modal' });
          } else {
            setError({ ...e, type: 'toast' });
          }
          ampli.loginUnsuccessful({ error_text: e.message || e.title });
        }
      }
    },
    [deviceService, setError]
  );

  const registerUser = useCallback(
    async ({
      ampliPromotionType,
      promoCode,
      userRegistrationObject,
    }: {
      ampliPromotionType: RegistrationReferralCodeEnteredSuccessfulProperties['promotion_type'];
      promoCode: string;
      userRegistrationObject: UserRegistrationRequest;
    }): Promise<void> => {
      let auth0RegistrationResponse;
      try {
        // auth0 - register, login
        auth0RegistrationResponse = await api.registerUserAuth0(userRegistrationObject);
        await api.loginUserAuth0({
          deviceId: deviceService.getDeviceId(),
          userLoginObject: {
            accepted_product_policies: userRegistrationObject.accepted_product_policies,
            user: {
              email: userRegistrationObject.user.email,
              password: userRegistrationObject.user.password,
              birthdate: userRegistrationObject.user.birthdate,
            },
          },
        });

        // Setting the user new registration state "feels" a bit out of place here.
        // Seems like we should _perhaps_ have a separate slice of state for auth-related state?
        // i.e. `isAuthenticated`, `isNewRegistration`, etc... distinct from user/profile data.
        // We can revisit later if needed.
        setUserNewRegistration();
        setIsAuthenticated(true);

        ampli.registrationBirthdayEnteredSuccessful();
        ampli.registrationPasswordEnteredSuccessful();
        ampli.registrationEmailEnteredSuccessful();
        ampli.registrationUsernameEnteredSuccessful();
        ampli.registrationReferralCodeEnteredSuccessful({
          promo_code: promoCode,
          promotion_type: ampliPromotionType,
        });
      } catch (e) {
        if (auth0RegistrationResponse) {
          // if auth0 sign up is successful, but login fails, show user custom error msg
          // and bring them to the login screen
          const errorMessage = 'Your account was successfully created. Please try logging in.';
          setError({
            message: errorMessage,
            type: 'toast',
          });
          navigate('/login');
          return;
        }
        setError({ ...e, type: 'toast' });
      }
    },
    [deviceService, navigate, setError, setUserNewRegistration]
  );

  const errInterceptor = async (axiosError: AxiosError<any, any>) => {
    // Check if error is caused by breached password detection
    const isPasswordLeaked = axiosError.response?.data?.error === ERROR_CODES.PASSWORD_LEAKED;

    if (isPasswordLeaked) {
      let errorMessage: string = '';
      if (axiosError?.config?.url?.includes('/signup')) {
        errorMessage = ERROR_MESSAGES.REGISTER_BREACHED_PASSWORD;
      } else if (axiosError?.config?.url?.includes('/token')) {
        errorMessage = ERROR_MESSAGES.LOGIN_BREACHED_PASSWORD;
      }

      setError({
        message: errorMessage || axiosError.response?.data?.error_description,
        type: 'toast',
      });

      return Promise.reject(
        new AppError({
          ...axiosError,
          message: errorMessage || axiosError.response?.data?.error_description,
        })
      );
    }

    // Check if error is a failed refresh token request (403, auth0)
    // This check examines the request body to determine if it was a refresh token request.
    // The `axiosError.config.data` property is a stringified version of the request body.
    const refreshTokenFailed =
      JSON.parse(axiosError?.config?.data || '{}').grant_type === 'refresh_token';

    if (refreshTokenFailed) {
      // If the refresh token request fails, we need to log the user out.
      await logoutUser();
      setError({
        message: ERROR_MESSAGES.UNAUTHORIZED_ERROR,
        type: 'toast',
      });
      return Promise.reject(
        new AppError({
          ...axiosError,
          message: ERROR_MESSAGES.UNAUTHORIZED_ERROR,
          title: 'Something went wrong',
        })
      );
    }

    const isUnauthenticatedResponse = axiosError?.response?.status === 401;

    // mobile apps will send a fresh access token before opening web views
    // the token is stored as session. web should never refresh it.
    // web should catch the 401 and direct the user back to the app
    if (isMobilePage && isUnauthenticatedResponse) {
      errorLogger(false, ERROR_MESSAGES.UNAUTHORIZED_ERROR_MOBILE, {
        url: axiosError.config.url,
      });
      return Promise.reject(
        new AppError({
          ...axiosError,
          status: 401,
          title: 'Something went wrong',
          message: ERROR_MESSAGES.UNAUTHORIZED_ERROR_MOBILE,
        })
      );
    }

    // try to get a new access token, then retry the original request
    // log user out if the original request fails
    // failed refresh token requests are caught in the block above
    if (isUnauthenticatedResponse) {
      let accessToken: string | undefined;
      try {
        await auth0Service.refreshAccessToken();
        accessToken = auth0Service.getAccessToken();
      } catch (e) {
        // this is a catch for the refresh token request
        // this error is handled above
        return Promise.reject(ERROR_MESSAGES.UNAUTHORIZED_ERROR);
      }

      if (accessToken) {
        // this is clean up for a bug introduced in FANGROWTH-871 -it removes the access token from cookies
        // lines 345-347 can be deleted after any current refresh tokens have definitively expired (6/1/2024)
        Cookies.remove('session');

        // retry logic
        const originalRequest = axiosError.config;
        originalRequest.headers.Authorization = accessToken;
        const originalRequestResponse = await axios(originalRequest);
        if (originalRequestResponse.status !== 401) {
          return Promise.resolve(originalRequestResponse);
        }
        await logoutUser();
        // we can't assume that all of our API calls are handled by a toast message
        // so we need to set this error AND bubble the message up to the original request
        setError({
          message: ERROR_MESSAGES.UNAUTHORIZED_ERROR,
          type: 'toast',
        });
        return Promise.reject(ERROR_MESSAGES.UNAUTHORIZED_ERROR);
      }
    }

    const signOutRoute = axiosError?.config?.url?.includes('/sign_out'); // devise

    if (signOutRoute) {
      // if a devise token is invalid or deleted, the sign_out api request will send a 403 or 500
      // we need to handle this error and show the user the correct toast message
      return Promise.reject(ERROR_MESSAGES.UNAUTHORIZED_ERROR);
    }

    // handle auth0 sign up error common password
    if (axiosError?.response?.data?.name === ERROR_NAMES.PASSWORD_DICTIONARY_ERROR) {
      return Promise.reject(
        new AppError({
          ...axiosError,
          message: ERROR_MESSAGES.DICTIONARY_ERROR,
        })
      );
    }
    // handle auth0 error invalid password
    if (axiosError?.response?.data?.name === ERROR_NAMES.PASSWORD_NO_USER_INFO_ERROR) {
      return Promise.reject(
        new AppError({
          ...axiosError,
          message: ERROR_MESSAGES.NO_USER_INFO_ERROR,
        })
      );
    }

    // handle auth0 errors, eg. invalid password on sign in
    if (axiosError?.response?.data?.description) {
      return Promise.reject(
        new AppError({
          ...axiosError,
          message:
            axiosError.response.data.friendly_message || axiosError.response.data.description,
        })
      );
    }

    // handle fantasy api errors
    return Promise.reject(axiosError);
  };

  const interceptor = axios.interceptors.response.use(undefined, errInterceptor);
  useEffect(() => {
    return () => axios.interceptors.response.eject(interceptor);
  });

  useEffect(() => {
    if (isLoading) {
      authService
        .isAuthenticated()
        .then((isAuthenticatedResult) => {
          setIsAuthenticated(isAuthenticatedResult);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isLoading, authService]);

  const values = useMemo(
    () => ({
      confirmMfaCode,
      isLoading,
      isAuthenticated,
      loginUser,
      logoutUser,
      registerUser,
    }),
    [confirmMfaCode, isLoading, isAuthenticated, loginUser, logoutUser, registerUser]
  );

  return (
    <AuthContext.Provider value={values}>
      {/*
        By showing the loader while auth is loading/resolving, we can prevent the UI
        from "flashing" between unauthenticated and authenticated states. This can
        save us from a lot of conditional code within components and UI shifting.
      */}
      {forceLogout || isLoading ? <Loader className={styles.loader} delay={1000} /> : children}
    </AuthContext.Provider>
  );
};

export default connect(
  () => ({}),
  (dispatch) => ({
    setError: (payload: Parameters<typeof setErrorAction>[0]) => dispatch(setErrorAction(payload)),
    setUser: (payload: Parameters<typeof setUserAction>[0]) => dispatch(setUserAction(payload)),
    setUserNewRegistration: () => dispatch(setUserNewRegistrationAction()),
  })
)(AuthContextProvider);

function clearStorage() {
  const underdogTheme = localStorage.getItem(UD_THEME);
  localStorage.clear();
  localStorage.setItem(UD_THEME, underdogTheme);
  sessionStorage.clear();
}

let isLoggingOut = false;
/**
 * This function should be used to log out the user without triggering
 * any redux state changes or redirects.
 */
async function logoutUserInternal({
  ampliService,
  authService,
  loggingService,
}: {
  ampliService: AmpliService | undefined;
  authService: AuthService;
  loggingService: LoggingService | undefined;
}) {
  // We want to prevent multiple (inadvertent) logout calls from
  // occurring simultaneously.
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  try {
    // If user does not have an Auth0 refresh token, then
    // we try to invalidate any legacy token the user may have by
    // calling the `api.logoutUser` function. This function
    // is not relevant for Auth0, so 99.99% of the time this code
    // will drop into the `finally` block.
    if (!Cookies.get(AUTH0_SESSION_REFRESH)) {
      await api.logoutUser();
    }
  } finally {
    isLoggingOut = false;

    // Logout from the authService first, that is the most critical
    // action for logging out. Then do any other cleanup.
    authService.logout();

    removeReferral();
    geoComplyManager.endUserSession();

    // batches all unsent events to Amplitude.
    ampliService?.batchAmplitudeEvents();

    loggingService?.clearUser();

    unsubscribeToPrivateChannels();
    clearStorage();
    eventLogger({
      gtm: {
        eventName: 'log_out',
      },
    });
    window.Intercom('shutdown');
  }
}
